import { createFileRoute } from '@tanstack/react-router'
import { ProductFrame } from '@/components/marketing/product-frame'
import { UptimeBarsPiece } from '@/components/marketing/ui-pieces'
import { FeaturePage } from '@/components/feature-page'

export const Route = createFileRoute('/_site/for/freelancers')({
  component: FreelancersPage,
  head: () => ({
    meta: [
      { title: 'Website Monitoring for Freelancers | Uptrack' },
      { name: 'description', content: 'Monitor every client website. Pro includes 50 monitors for freelancers managing client sites. Email, Slack, and Discord alerts. Status pages per client.' },
      { property: 'og:title', content: 'Website Monitoring for Freelancers | Uptrack' },
      { property: 'og:description', content: 'Monitor every client site. Pro has 50 monitors at 30s, status pages and multi-channel alerts, from $3/mo.' },
      { property: 'og:image', content: 'https://uptrack.app/og/for-freelancers.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/for/freelancers' }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://uptrack.app' },
            { '@type': 'ListItem', position: 2, name: 'For Freelancers', item: 'https://uptrack.app/for/freelancers' },
          ],
        }),
      },
    ],
  }),
})

function FreelancersPage() {
  return (
    <FeaturePage
      section="Freelancer website monitoring"
      eyebrow="For · Freelancer website monitoring"
      title="Monitor every client site. Reliable."
      intro='50 monitors on Pro — enough for 10-20 client websites. Get alerted the moment a site goes down, share a status page with each client, and look like a pro. Choose the plan that fits your client portfolio.'
      featuresHeading="Everything freelancers need in one place"
      features={[
        { title: '50 monitors on Pro', desc: 'Perfect for freelancers with 10-20 client sites. Monitor each one from one dashboard.' },
        { title: 'Free to start', desc: 'Start free with 5 monitors, then choose Pro, Team, or Business as your client portfolio grows.' },
        { title: 'Status pages per client', desc: 'Create a branded status page for each client. Share a link so they can see uptime without asking you.' },
        { title: 'Email + Slack + Discord', desc: 'Get notified where you already work. Set up alerts in email, Slack, Discord, or all three.' },
        { title: 'Setup in 1 minute', desc: 'Enter a URL, pick a check interval, done. No configuration files, no agents to install.' },
        { title: 'Look professional', desc: 'Proactively monitoring client sites shows you care. Fix issues before clients even notice them.' },
      ]}
      why={{ heading: 'Be the freelancer who catches problems first', body: 'Your client\'s website goes down at 2am. Instead of waking up to an angry email, you get an alert, fix it, and send a quick update: "Your site had a brief issue — already resolved." That\'s the kind of service that gets you referrals.' }}
      cta={{ heading: '30-second checks on every plan', body: 'Monitor all your client sites. Start free, or choose a plan for more.' }}
      visual={
        <ProductFrame url="uptrack.app/dashboard">
          <UptimeBarsPiece />
        </ProductFrame>
      }
    />
  )
}
